/**
 * Already-ordered lookups derived from a week's submitted forecast batches.
 *
 * Drives the "N already ordered" subtext under each table cell and the
 * per-(mealtime, grade) count chips on a collapsed day card. Pure — built
 * once from the batch rows, then queried by the components.
 */

import type { FamilyOrderCellRow, Mealtime, SchoolForecastBatchRow } from '@/api/school-orders'

import { WHOLE_SCHOOL_COLUMN_ID } from './columns'

/**
 * `audienceId` — the column-attribution id an item is keyed by: the portion
 * tier (`GradeRange.id`) for age-group items, the `(dimension, value)`
 * tuple key for group items (`OrderColumn.audienceId` mirrors this on the
 * render side).
 */
function cellKey(dateKey: string, mealtime: Mealtime, audienceId: string, menuItemId: string): string {
  return `${dateKey}::${mealtime}::${audienceId}::${menuItemId}`
}

function mealtimeGradeKey(dateKey: string, mealtime: Mealtime, audienceId: string): string {
  return `${dateKey}::${mealtime}::${audienceId}`
}

/**
 * One placed parent order line, as the day panel enumerates them —
 * the source for field-trip "extra rows" (placed lines whose menu item
 * is NOT in the school's browse grid, e.g. bagged menus hidden from the
 * school projection).
 */
export interface PlacedDayLine {
  mealtime: Mealtime
  menuItemId: string
  /** Optional server enrichment; null on older payloads. */
  itemName: string | null
  /** Column attribution (portion tier / tuple key / whole-school); null when unattributable. */
  audienceId: string | null
  quantity: number
  /**
   * The line's menu item is a system milk row (`lineupMilkOptionCode`) —
   * a placed bagged menu's milk surfaces as a milk extra row, not a meal.
   */
  systemMilk: boolean
  /**
   * The line is a genuine field-trip line (server `fieldTrip`). Lines off
   * the grid for any other reason — e.g. an item dropped from the menu —
   * still render as extra rows, but never as a field trip. Absent on older
   * payloads, which keep the pre-flag reading (off-grid = bagged).
   */
  fieldTrip: boolean
}

export interface AlreadyOrdered {
  /** Quantity already ordered for a specific (date, mealtime, column audience, item). */
  cellQty(dateKey: string, mealtime: Mealtime, audienceId: string, menuItemId: string): number
  /**
   * Committed PARENT (FAMILY_DIRECT) quantity for the same cell — the
   * "+X parent orders" legend on the placed-day summary. 0 when the
   * builder got no `familyOrders` rows (older cached payloads).
   */
  familyCellQty(dateKey: string, mealtime: Mealtime, audienceId: string, menuItemId: string): number
  /**
   * Committed PARENT meal count for the whole day: every FAMILY_DIRECT
   * cell, attributed to a column or not, with system-milk lines excluded
   * (display meal counts, as in `mealtimeGradeQty`). The collapsed card's
   * "+X parent orders" summary on a read-only surface — a PARENT-only
   * campus has no school order, so parent demand is all it can show.
   * 0 when the builder got no `familyOrders` rows.
   */
  familyDayQty(dateKey: string): number
  /** Every placed parent line for a day, in batch order. */
  dayLines(dateKey: string): PlacedDayLine[]
  /** Quantity already ordered across all items for a (date, mealtime, column audience). */
  mealtimeGradeQty(dateKey: string, mealtime: Mealtime, audienceId: string): number
  /** Total quantity ordered across the whole day — >0 means the day is placed. */
  dayQty(dateKey: string): number
  /** The placed batch id for a (date, mealtime), or null if nothing is ordered. */
  batchId(dateKey: string, mealtime: Mealtime): string | null
  /**
   * Distinct placer names across the day's batches, in batch order.
   * School orders are account-owned (any admin can edit) — the UI names
   * the original placer so the shared model is legible. Empty when
   * nothing is placed or the rows predate the `placedBy` enrichment.
   */
  dayPlacedByNames(dateKey: string): string[]
  /**
   * Addendum G — the placed per-kind Meal-parts breakdown for a cell, or
   * null on legacy lines. Seeds the edit buffer so custom breakdowns
   * round-trip.
   */
  cellBreakdown(
    dateKey: string,
    mealtime: Mealtime,
    audienceId: string,
    menuItemId: string,
  ): Record<string, number> | null
  /**
   * PROVENANCE for `cellBreakdown` — the ComponentKinds a human TYPED on
   * the placed line. `null` on legacy lines, which the editor treats as
   * "all typed" so no quantity moves without an explicit reset; `[]` means
   * every number was rate-derived and re-derives on a pre-deadline edit.
   */
  cellTypedKinds(dateKey: string, mealtime: Mealtime, audienceId: string, menuItemId: string): string[] | null
  /**
   * True when the placed line's OWN quantity was system-DERIVED — the
   * menu's system milk rows, whose count tracks the meal counts through
   * the campus's BEVERAGE rate. Those re-derive on a pre-deadline edit;
   * a hand-typed line never does.
   */
  cellQtyIsDerived(dateKey: string, mealtime: Mealtime, audienceId: string, menuItemId: string): boolean
  /**
   * Chef pre-approved swap: the placed line's re-rendered name for this
   * (date, mealtime, item), or null when nothing was swapped. Must win
   * over the live-menu name — the menu still carries the original
   * component.
   */
  substitutedName(dateKey: string, mealtime: Mealtime, menuItemId: string): string | null
  /**
   * Quantity with NO column attribution for a (date, mealtime, item) —
   * lines whose diner resolves to no portion tier and no group tuple
   * (e.g. staff self-diner orders, whose memberships carry no enrollment
   * and therefore no grade). These are structurally invisible to `cellQty`
   * (there is no column to land in), so printed sheets that total across
   * columns MUST add this back or they silently undercount (d298a81b).
   * Family rows count only under `combineFamilyIntoTotals`, mirroring
   * `cellQty`, so the catch column always agrees with the attributed
   * cells beside it. Always 0 in `wholeSchool` mode, where every line
   * attributes to the single column.
   */
  unattributedCellQty(dateKey: string, mealtime: Mealtime, menuItemId: string): number
  /**
   * The STAFF-owned share of `unattributedCellQty` — family lines whose
   * diner the server classified as campus staff (`unattributedDinerKind`).
   * The print sheet labels its catch columns with this split ("Staff" for
   * these, "Other" for the remainder) instead of a blanket "Unassigned".
   * School batch lines carry no diner and never count here.
   */
  unattributedStaffCellQty(dateKey: string, mealtime: Mealtime, menuItemId: string): number
  /**
   * Unattributed MEAL count for a (date, mealtime) — `unattributedCellQty`
   * summed across items, with system-milk lines excluded, mirroring
   * `mealtimeGradeQty`'s display-meal-count rule.
   */
  unattributedMealtimeQty(dateKey: string, mealtime: Mealtime): number
}

/**
 * Sum several campuses' AlreadyOrdered views into one — the collapsed week
 * list's district-wide totals (Sheila, 2026-08-24: a collapsed card showing
 * ONE campus's numbers, whichever the slider last touched, has no use case
 * and read as the district total). Numeric lookups sum; day lines concatenate
 * in campus order; batch ids and substituted names resolve to the first
 * campus that has one (callers needing per-campus identity use the campus's
 * own view); breakdown/typed/derived provenance is per-campus editing state
 * and reports the first campus that placed the cell.
 */
export function aggregateAlreadyOrdered(parts: ReadonlyArray<AlreadyOrdered>): AlreadyOrdered {
  if (parts.length === 1) {
    return parts[0]!
  }
  const sum = (pick: (part: AlreadyOrdered) => number): number => parts.reduce((acc, part) => acc + pick(part), 0)
  const first = <T>(pick: (part: AlreadyOrdered) => T | null): T | null => {
    for (const part of parts) {
      const value = pick(part)
      if (value !== null) {
        return value
      }
    }
    return null
  }
  return {
    cellQty: (d, m, a, i) => sum((p) => p.cellQty(d, m, a, i)),
    familyCellQty: (d, m, a, i) => sum((p) => p.familyCellQty(d, m, a, i)),
    familyDayQty: (d) => sum((p) => p.familyDayQty(d)),
    dayLines: (d) => parts.flatMap((p) => p.dayLines(d)),
    mealtimeGradeQty: (d, m, a) => sum((p) => p.mealtimeGradeQty(d, m, a)),
    dayQty: (d) => sum((p) => p.dayQty(d)),
    batchId: (d, m) => first((p) => p.batchId(d, m)),
    dayPlacedByNames: (d) => [...new Set(parts.flatMap((p) => p.dayPlacedByNames(d)))],
    cellBreakdown: (d, m, a, i) => first((p) => p.cellBreakdown(d, m, a, i)),
    cellTypedKinds: (d, m, a, i) => first((p) => p.cellTypedKinds(d, m, a, i)),
    cellQtyIsDerived: (d, m, a, i) => parts.some((p) => p.cellQtyIsDerived(d, m, a, i)),
    substitutedName: (d, m, i) => first((p) => p.substitutedName(d, m, i)),
    unattributedCellQty: (d, m, i) => sum((p) => p.unattributedCellQty(d, m, i)),
    unattributedStaffCellQty: (d, m, i) => sum((p) => p.unattributedStaffCellQty(d, m, i)),
    unattributedMealtimeQty: (d, m) => sum((p) => p.unattributedMealtimeQty(d, m)),
  }
}

function mealtimeKey(dateKey: string, mealtime: Mealtime): string {
  return `${dateKey}::${mealtime}`
}

/**
 * Sum submitted batch item quantities into per-cell and per-(mealtime,column)
 * lookups. Non-AGE_GROUP items attribute by their
 * `(groupDimension, groupValue)` tuple key (mirroring
 * `OrderColumn.audienceId`); AGE_GROUP items attribute by `gradeRangeId`
 * (the portion tier). Items with no attribution stay out of the per-column
 * lookups (there is no column to land in) and accumulate separately under
 * `unattributedCellQty` / `unattributedMealtimeQty` — except in
 * `wholeSchool` mode (NONE campuses), where every item attributes to the
 * single whole-school column regardless of any stamped tier or tuple.
 */
export function buildAlreadyOrdered(
  batches: ReadonlyArray<SchoolForecastBatchRow>,
  options?: {
    wholeSchool?: boolean
    /** Parent-demand overlay rows (/forecast-batches `familyOrders`). */
    familyOrders?: ReadonlyArray<FamilyOrderCellRow>
    /**
     * Cafeteria "Orders lookup": ADD the parent quantities into the main
     * lookups (`cellQty` / `mealtimeGradeQty` / `dayQty`) so every reader
     * shows school + parent COMBINED totals — a day with only parent
     * orders counts as ordered. `familyCellQty` stays the separate
     * parent-only number either way. System-milk family cells are kept
     * out of `mealtimeGradeQty` (display meal counts), mirroring the
     * school batch items' systemMilk rule.
     */
    combineFamilyIntoTotals?: boolean
    /**
     * Audience fold (menu-derived band columns): maps a placed line's
     * portion-tier id onto the audience its MEALTIME's partition renders
     * (`bandAudienceFold`) — so orders placed before the band cutover
     * (keyed k-5 / 6-8 / age tiers) still display, and lunch-band tiers
     * surface under breakfast's K-12 column. Identity for unmapped ids.
     * Group-tuple and whole-school audiences never fold.
     */
    foldTierAudience?: (mealtime: Mealtime, tierId: string) => string
  },
): AlreadyOrdered {
  const foldTier = (mealtime: Mealtime, tierId: string | null): string | null =>
    tierId === null ? null : (options?.foldTierAudience?.(mealtime, tierId) ?? tierId)
  const byCell = new Map<string, number>()
  const byMealtimeGrade = new Map<string, number>()
  const byDay = new Map<string, number>()
  // Lines with NO attribution get a separate catch bucket instead of a
  // band: a diner with no portion tier or group tuple (staff self-diner
  // orders are the main class) structurally cannot land in a column, but
  // dropping the quantity made printed order sheets undercount real
  // demand (d298a81b). Keyed per (day, mealtime, item) and per
  // (day, mealtime); the per-column maps above are untouched.
  const unattributedByCell = new Map<string, number>()
  // The staff-owned share of unattributedByCell, keyed identically — family
  // lines whose diner the server classified as campus staff. Kept as a
  // second map (not a struct) so the total stays one lookup.
  const unattributedStaffByCell = new Map<string, number>()
  const unattributedByMealtime = new Map<string, number>()
  const addUnattributed = (
    dateKey: string,
    mealtime: Mealtime,
    menuItemId: string,
    quantity: number,
    systemMilk: boolean,
    staff: boolean,
  ): void => {
    const uk = `${mealtimeKey(dateKey, mealtime)}::${menuItemId}`
    unattributedByCell.set(uk, (unattributedByCell.get(uk) ?? 0) + quantity)
    if (staff) {
      unattributedStaffByCell.set(uk, (unattributedStaffByCell.get(uk) ?? 0) + quantity)
    }
    // Display meal counts exclude system-milk lines, mirroring byMealtimeGrade.
    if (!systemMilk) {
      const umk = mealtimeKey(dateKey, mealtime)
      unattributedByMealtime.set(umk, (unattributedByMealtime.get(umk) ?? 0) + quantity)
    }
  }
  // Parent-demand cells bucket by the IDENTICAL audience rule as batch
  // items below, so the two lookups always agree on a column.
  const familyByCell = new Map<string, number>()
  const familyMealsByDay = new Map<string, number>()
  for (const row of options?.familyOrders ?? []) {
    const dateKey = row.serviceDate.slice(0, 10)
    // Keyed like batch items below — by the successor item when a dated
    // template release replaced the parent line's menu (PROD-1515).
    const menuItemId = row.currentMenuItemId ?? row.menuItemId
    // Counted before the attribution branch: a staff self-order with no
    // grade lands in no column, but it is still an order placed that day.
    if (!row.systemMilk) {
      familyMealsByDay.set(dateKey, (familyMealsByDay.get(dateKey) ?? 0) + row.quantity)
    }
    const audienceId = options?.wholeSchool
      ? WHOLE_SCHOOL_COLUMN_ID
      : row.groupDimension && row.groupDimension !== 'AGE_GROUP' && row.groupValue
        ? `${row.groupDimension}::${row.groupValue}`
        : row.groupDimension === 'AGE_GROUP' && !row.gradeRangeId && row.groupValue
          ? row.groupValue // tier-less sentinel column (ALL_GRADES)
          : foldTier(row.mealtime, row.gradeRangeId)
    if (options?.combineFamilyIntoTotals) {
      byDay.set(dateKey, (byDay.get(dateKey) ?? 0) + row.quantity)
    }
    if (!audienceId) {
      // Combined-totals consumers only, mirroring byCell/byMealtimeGrade —
      // without the flag, family quantities stay out of every combined lookup.
      if (options?.combineFamilyIntoTotals) {
        addUnattributed(
          dateKey,
          row.mealtime,
          menuItemId,
          row.quantity,
          row.systemMilk === true,
          row.unattributedDinerKind === 'STAFF',
        )
      }
      continue
    }
    const ck = cellKey(dateKey, row.mealtime, audienceId, menuItemId)
    familyByCell.set(ck, (familyByCell.get(ck) ?? 0) + row.quantity)
    if (options?.combineFamilyIntoTotals) {
      byCell.set(ck, (byCell.get(ck) ?? 0) + row.quantity)
      if (!row.systemMilk) {
        const mk = mealtimeGradeKey(dateKey, row.mealtime, audienceId)
        byMealtimeGrade.set(mk, (byMealtimeGrade.get(mk) ?? 0) + row.quantity)
      }
    }
  }
  const batchIdByMealtime = new Map<string, string>()
  const breakdownByCell = new Map<string, Record<string, number>>()
  // Provenance, keyed like the breakdown. A cell is only "all typed" when
  // EVERY contributing line says so — a legacy line (null) in the mix keeps
  // the whole cell legacy, which is the conservative direction.
  const typedKindsByCell = new Map<string, string[] | null>()
  const derivedQtyByCell = new Map<string, boolean>()
  const namesByDay = new Map<string, string[]>()
  const linesByDay = new Map<string, PlacedDayLine[]>()
  const substitutedNameByLine = new Map<string, string>()
  for (const batch of batches) {
    // Cancelled batches are not "already ordered" — they must not seed
    // cell/day counts, show "Order placed", or resolve a batchId (which
    // drives edit mode and cancel targeting). Neither are DRAFT batches:
    // a failed-debit submit leaves the batch DRAFT/UNPAID, and counting
    // it would lock the day read-only for every other admin. The server
    // excludes both from /forecast-batches; the guard stays for stale
    // caches. Mirrors the family already-ordered guard (#1150).
    if (batch.status === 'VOIDED' || batch.status === 'DRAFT') {
      continue
    }
    const dateKey = batch.serviceDate.slice(0, 10)
    const placerName = batch.placedBy?.name
    if (placerName) {
      const names = namesByDay.get(dateKey) ?? []
      if (!names.includes(placerName)) {
        names.push(placerName)
      }
      namesByDay.set(dateKey, names)
    }
    batchIdByMealtime.set(mealtimeKey(dateKey, batch.mealtime), batch.id)
    for (const item of batch.items) {
      // PROD-1515: a line placed before a dated template release points at
      // the ENDED menu's item; key it by the successor item the grid lists,
      // or it matches no row and shows up as a stray "extra" row.
      const menuItemId = item.currentMenuItemId ?? item.menuItemId
      // Day-level total powers "is this day placed?" — count every item so a
      // placed day is detected even if an item lacks a column attribution.
      byDay.set(dateKey, (byDay.get(dateKey) ?? 0) + item.quantity)
      const audienceId = options?.wholeSchool
        ? WHOLE_SCHOOL_COLUMN_ID
        : item.groupDimension && item.groupDimension !== 'AGE_GROUP' && item.groupValue
          ? `${item.groupDimension}::${item.groupValue}`
          : item.groupDimension === 'AGE_GROUP' && !item.gradeRangeId && item.groupValue
            ? item.groupValue // tier-less sentinel column (ALL_GRADES)
            : foldTier(batch.mealtime, item.gradeRangeId)
      // Enumerable line list (field-trip extra rows) — recorded even when
      // the line has no column attribution, so a bagged line placed
      // without one still surfaces in the day grid.
      const dayLineList = linesByDay.get(dateKey) ?? []
      dayLineList.push({
        mealtime: batch.mealtime,
        menuItemId,
        itemName: item.itemName ?? null,
        audienceId,
        quantity: item.quantity,
        systemMilk: item.systemMilk === true,
        fieldTrip: item.fieldTrip !== false,
      })
      linesByDay.set(dateKey, dayLineList)
      if (item.itemNameSubstituted && item.itemName) {
        substitutedNameByLine.set(`${mealtimeKey(dateKey, batch.mealtime)}::${menuItemId}`, item.itemName)
      }
      if (!audienceId) {
        // School batch lines are bulk demand with no diner — never staff.
        addUnattributed(dateKey, batch.mealtime, menuItemId, item.quantity, item.systemMilk === true, false)
        continue
      }
      const ck = cellKey(dateKey, batch.mealtime, audienceId, menuItemId)
      byCell.set(ck, (byCell.get(ck) ?? 0) + item.quantity)
      // Per-(mealtime, grade) counts are DISPLAY meal counts (collapsed
      // day-card pills, "N ordered" tooltips) — system-milk lines derive
      // 1:1 from the meals and would read every placed count back doubled.
      // `byDay` above stays all-lines: it only gates "is this day placed?".
      if (!item.systemMilk) {
        const mk = mealtimeGradeKey(dateKey, batch.mealtime, audienceId)
        byMealtimeGrade.set(mk, (byMealtimeGrade.get(mk) ?? 0) + item.quantity)
      }
      // A DERIVED line's quantity is a pure function of the meal counts and
      // the campus's BEVERAGE rate, so an edit may recompute it.
      //
      // ANDed across every line contributing to the cell: one hand-typed line
      // makes the whole cell typed. Setting `true` on the first DERIVED line
      // and never clearing it would let a cell holding both a derived and a
      // typed line be recomputed, overwriting a number a person entered —
      // the one direction this change is careful never to move.
      derivedQtyByCell.set(ck, (derivedQtyByCell.get(ck) ?? true) && item.quantitySource === 'DERIVED')
      if (item.componentBreakdown) {
        // Union the typed sets across contributing lines; a single legacy
        // line (undefined/null) makes the cell legacy.
        if (item.componentTypedKinds == null) {
          typedKindsByCell.set(ck, null)
        } else if (typedKindsByCell.get(ck) !== null || !typedKindsByCell.has(ck)) {
          const merged = new Set([...(typedKindsByCell.get(ck) ?? []), ...item.componentTypedKinds])
          typedKindsByCell.set(ck, [...merged])
        }
        // Same cell across batches sums per kind (defensive — one line per
        // cell is the normal shape).
        const merged = { ...(breakdownByCell.get(ck) ?? {}) }
        for (const [kind, qty] of Object.entries(item.componentBreakdown)) {
          merged[kind] = (merged[kind] ?? 0) + qty
        }
        breakdownByCell.set(ck, merged)
      }
    }
  }
  return {
    cellQty: (dateKey, mealtime, gradeRangeId, menuItemId) =>
      byCell.get(cellKey(dateKey, mealtime, gradeRangeId, menuItemId)) ?? 0,
    familyCellQty: (dateKey, mealtime, gradeRangeId, menuItemId) =>
      familyByCell.get(cellKey(dateKey, mealtime, gradeRangeId, menuItemId)) ?? 0,
    familyDayQty: (dateKey) => familyMealsByDay.get(dateKey) ?? 0,
    mealtimeGradeQty: (dateKey, mealtime, gradeRangeId) =>
      byMealtimeGrade.get(mealtimeGradeKey(dateKey, mealtime, gradeRangeId)) ?? 0,
    dayQty: (dateKey) => byDay.get(dateKey) ?? 0,
    dayLines: (dateKey) => linesByDay.get(dateKey) ?? [],
    batchId: (dateKey, mealtime) => batchIdByMealtime.get(mealtimeKey(dateKey, mealtime)) ?? null,
    dayPlacedByNames: (dateKey) => namesByDay.get(dateKey) ?? [],
    cellBreakdown: (dateKey, mealtime, gradeRangeId, menuItemId) =>
      breakdownByCell.get(cellKey(dateKey, mealtime, gradeRangeId, menuItemId)) ?? null,
    cellTypedKinds: (dateKey, mealtime, gradeRangeId, menuItemId) =>
      typedKindsByCell.get(cellKey(dateKey, mealtime, gradeRangeId, menuItemId)) ?? null,
    cellQtyIsDerived: (dateKey, mealtime, gradeRangeId, menuItemId) =>
      derivedQtyByCell.get(cellKey(dateKey, mealtime, gradeRangeId, menuItemId)) ?? false,
    substitutedName: (dateKey, mealtime, menuItemId) =>
      substitutedNameByLine.get(`${mealtimeKey(dateKey, mealtime)}::${menuItemId}`) ?? null,
    unattributedCellQty: (dateKey, mealtime, menuItemId) =>
      unattributedByCell.get(`${mealtimeKey(dateKey, mealtime)}::${menuItemId}`) ?? 0,
    unattributedStaffCellQty: (dateKey, mealtime, menuItemId) =>
      unattributedStaffByCell.get(`${mealtimeKey(dateKey, mealtime)}::${menuItemId}`) ?? 0,
    unattributedMealtimeQty: (dateKey, mealtime) => unattributedByMealtime.get(mealtimeKey(dateKey, mealtime)) ?? 0,
  }
}
